import { ReactNode } from 'react';

import { ADDRESS_REGEX, AddressRegexMatch, StreetRegexMatch } from '@/constants/regex.constants';
import type { PlaceDetails, PlaceTextMatch } from '@/services/placesApi/placesApi.types';
import { getOr } from '@/utils/getOr';

import { InputAddressAutocompleteAddress } from './FormikInputAddressAutocomplete';

export const boldMatchCharacters = (description: string, matches: PlaceTextMatch[]): ReactNode => {
  if (matches.length) {
    const boldedParts: ReactNode[] = [];
    let lastIndex = 0;

    matches.forEach(({ startOffset = 0, endOffset }) => {
      if (startOffset > lastIndex) {
        boldedParts.push(description.substring(lastIndex, startOffset));
      }
      boldedParts.push(<mark>{description.substring(startOffset, endOffset)}</mark>);
      lastIndex = endOffset;
    });
    if (lastIndex < description.length) {
      boldedParts.push(description.substring(lastIndex));
    }
    return <>{boldedParts}</>;
  }
  return description;
};

export const formikInputAddressToString = (address: InputAddressAutocompleteAddress): string => {
  let result = `${address.street}, ${address.suburb}, ${address.state}, ${address.postcode}`;

  if (address.apartment) {
    result = `${address.apartment}/${result}`;
  }

  return result;
};

export const extractAddressFromManuallyInput = (place: string) => {
  let street;
  let apartment;
  let suburb;
  let postcode;
  let state;

  if (!place) return { street, apartment, suburb, state, postcode };

  const match = [...place.matchAll(ADDRESS_REGEX)][0];
  const extractObj = getOr(match, 'groups', null);
  if (extractObj?.c_street_type) {
    street = [
      getOr(extractObj, 'c_house_number', ''),
      getOr(extractObj, 'c_street_name', ''),
      getOr(extractObj, 'c_street_type', ''),
    ]
      .filter((param) => param !== '')
      .join(' ');
  }
  if (extractObj?.c_level_type) {
    apartment = [getOr(extractObj, 'c_level_type', ''), getOr(extractObj, 'c_level_number', '')]
      .filter((param) => param !== '')
      .join(' ');
  }
  if (extractObj?.c_unit_type) {
    apartment = [
      `${(apartment ? `${apartment}, ` : '') + getOr(extractObj, 'c_unit_type', '')}`,
      getOr(extractObj, 'c_unit_number', ''),
    ]
      .filter((param) => param !== '')
      .join(' ');
  } else if (extractObj?.c_unit_number) {
    apartment = extractObj.c_unit_number;
  }

  if (extractObj?.c_suburb) {
    suburb = extractObj.c_suburb;
  }

  if (extractObj?.c_state) {
    state = extractObj.c_state;
  }

  if (extractObj?.c_postcode) {
    postcode = extractObj.c_postcode;
  }

  return { street, apartment, suburb, state, postcode };
};

export const extractAddressFromPlaceDetails = (place: PlaceDetails | null, placeDescription: string) => {
  let street;
  let apartment;
  let suburb;
  let postcode;
  let state;

  if (!place) return { street, apartment, suburb, state, postcode };

  for (const component of place.addressComponents ?? []) {
    const componentType: string = component.types[0] === 'political' ? component.types[1] : component.types[0];

    switch (componentType) {
      case 'subpremise': {
        apartment = `${component.longText}`;
        break;
      }
      case 'street_number': {
        street = `${component.longText}`;
        break;
      }
      case 'route': {
        street = street ? `${street} ${component.longText}` : `${component.longText}`;
        break;
      }
      case 'postal_code': {
        postcode = `${component.longText}`;
        break;
      }
      case 'locality':
        suburb = component.longText;
        break;

      case 'administrative_area_level_1': {
        state = component.shortText;
        break;
      }
      default:
        break;
    }
  }
  // Compared the user-entered address with the returned result from GG. if the subpremise missing, they're added
  const regex = RegExp(`^(.*?)/(.*?) ${street?.split(' ', 1)[0]}`); // get all the user entered values before a match with the first word from the Google result
  const result = regex.exec(placeDescription);

  if (Array.isArray(result)) {
    const unitNumber = result[1];
    const streetNumber = result[2];
    street = `${streetNumber} ${street}`; // add the street name to the user-entered unit & street number
    apartment = unitNumber;
  }
  return { street, apartment, suburb, postcode, state };
};

const extractAddressData = (value: string) => {
  const matches = [...(value ?? '').matchAll(ADDRESS_REGEX)][0];
  const extractObj: StreetRegexMatch = getOr(matches, 'groups', {}) as AddressRegexMatch;
  return extractObj;
};

export const validateStreetLength = (value: string | undefined): boolean => {
  const maxLengthNumber = 20;
  const minLengthName = 1;
  const maxLengthName = 50;
  const data = extractAddressData(value ?? '');
  const streetNumber = `${data.c_house_number ?? ''}`;
  const streetName = [data.c_street_name, data.c_street_type, data.c_trailing]
    .filter((value) => typeof value !== 'undefined')
    .join(' ');

  return (
    streetNumber.length <= maxLengthNumber && streetName.length >= minLengthName && streetName.length <= maxLengthName
  );
};
